import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonList,
  IonPage,
  IonRow,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { chevronBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { getLabsSelector } from '../selectors/labs.selector'
import { ILabMetadata, ILabsMetadata, ILabsState } from '../store/labs.types'
import { saveLabs, setDateTime, setLabValue } from '../store/labs.actions'
import ProcedureFooterComponent from '../../../../components/procedure/ProcedureFooter.component'
import ProcedureHeaderComponent from '../../../../components/procedure/ProcedureHeader.component'
import { getPatientTimelineSelector } from '../../../timeline/selectors/patientTimeline.selector'
import { getLabsConfigOptionsSelector } from '../../../options/selectors/labsConfigOptions.selector'
import { ILabsOptionsState } from '../../../options/store/options.types'
import { AccordianComponent } from '../../../../components/accordian/Accordian.components'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import './labs.page.css'
import { LabsConstants } from '../store/labs.constants'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { IOption } from '../../../../common/common.types'
import { allSpecialChars } from '../../../../components/numberPad/NumberPad.types'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialLabsState } from '../store/labs.reducer'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'

const Labs: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const labsConfig: ILabsOptionsState = useSelector(getLabsConfigOptionsSelector)
  const { labsConfigOptions, abgAccessOptions } = labsConfig
  const labsState: ILabsState = useSelector(getLabsSelector)
  const {
    date,
    time,
    assessedBy,
    pta,
    rolePerforming,
    rolePerformingPNNV,
    ABG_ACCESS,
    authorization,
    procedureComplications,
    comments,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = labsState
  const timelineState = useSelector(getPatientTimelineSelector)
  const { procedureLoader } = timelineState
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  useIonViewWillEnter(() => {
    if (date || time) dispatch(setDateTime(time, date))
  })

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'labs/SET_TIME', time: time })
              dispatch({ type: 'labs/SET_DATE', date: date })
              dispatch(saveLabs())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: labsState.key,
                rowActionType: TimelineConstants.SET_LABS_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(labsState, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialLabsState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onValueChange = (numbers: string[], labMd: ILabMetadata) => {
    if (labMd.columnName) dispatch(setLabValue(labMd.columnName.toLocaleUpperCase().trim(), numbers.join('')))
  }

  const setAccessOnChange = (option: IOption) => {
    dispatch(setLabValue(LabsConstants.ABG_ACCESS, option))
  }

  const getCheckMarkValue = (labsMetadata: ILabsMetadata): boolean => {
    return labsMetadata.some(
      (labMd) => labsState[labMd.columnName?.toLocaleUpperCase().trim() as keyof typeof labsState]
    )
  }

  const getLabValue = (columnName: string) => {
    if (columnName) {
      const labValue = labsState[columnName.toLocaleUpperCase().trim() as keyof typeof labsState]
      if (labValue && typeof labValue === 'string') {
        return labValue.split('')
      }
      return []
    }
    return []
  }

  const getLabel = (labMd: ILabMetadata) => {
    if (showUnits(labMd.defaultUnits)) return `${labMd.name} (${labMd.defaultUnits})`
    return labMd.name
  }
  const showUnits = (units: string | undefined) => {
    return units && units.indexOf(';') === -1
  }

  const getCategoryLabel = (category: string) => {
    if (category === LabsConstants.METABOLIC_CATRGORY) return `${t('labs.Metabolic')}`
    else if (category === LabsConstants.CARDIAC_CATRGORY) return `${t('labs.Cardiac')}`
    else if (category === LabsConstants.CBC_CATRGORY) return `${t('labs.CBC')}`
    else if (category === LabsConstants.BLOOD_GASSES_CATRGORY) return `${t('labs.BloodGasses')}`
    else if (category === LabsConstants.COAGULATION_CATRGORY) return `${t('labs.Coagulation')}`
    else if (category === LabsConstants.OTHER_CATRGORY) return `${t('labs.Other')}`
    else return category
  }

  function getLabGroupContent(labsMetadata: ILabsMetadata, category: string) {
    return (
      <>
        {labsMetadata.map((labMd) => {
          return (
            <>
              <IonRow key={labMd.id} className='mb-1'>
                <IonCol className='picklist'>
                  <NumberPad
                    dataTestId={labMd.columnName}
                    elementId={labMd.columnName}
                    label={getLabel(labMd)}
                    values={getLabValue(labMd.columnName)}
                    onNumPadClick={(numbers) => onValueChange(numbers, labMd)}
                    validCharacters={allSpecialChars}
                  />
                </IonCol>
              </IonRow>
              <hr key={`${labMd.id}_hr`} className='field-divider' />
            </>
          )
        })}
        {category === LabsConstants.BLOOD_GASSES_CATRGORY && (
          <>
            <IonRow key={LabsConstants.ABG_ACCESS} className='mb-1'>
              <IonCol className='picklist ion-no-padding font-roboto'>
                <SingleSelect
                  dataTestID='ABG_ACCES'
                  selectedOption={ABG_ACCESS}
                  listHeader={t('labs.ABG_ACCESS')}
                  options={abgAccessOptions}
                  action={setAccessOnChange}
                  label={t('labs.ABG_ACCESS')}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
      </>
    )
  }

  return (
    <IonPage id='LabsPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('labs.Labs')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent id='LabsContent' color='light'>
        <>
          {procedureLoader ? (
            <IonSpinner className='procedure-loader' data-testid='Spinner' />
          ) : (
            <IonList id='LabsProcedure'>
              <ProcedureHeaderComponent
                isPHI
                procedureType={'labs'}
                procedureTime={time}
                procedureDate={date}
                assessedBy={assessedBy}
                rolePerforming={rolePerforming}
                rolePerformingPNNV={rolePerformingPNNV}
                hideAttemptField
                hideSuccessfulAttempt
                pta={pta}
                isEkgImport={isEkgImport}
                procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
                procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
              />
              <IonGrid>
                {labsConfigOptions.map((labsConfig) => {
                  return (
                    <IonRow key={labsConfig.category}>
                      <IonCol className='ion-no-padding'>
                        <AccordianComponent
                          dataTestId={labsConfig.category}
                          heading={getCategoryLabel(labsConfig.category)}
                          content={getLabGroupContent(labsConfig.labsMetadata, labsConfig.category)}
                          isCheckMark={getCheckMarkValue(labsConfig.labsMetadata)}
                        />
                      </IonCol>
                    </IonRow>
                  )
                })}
              </IonGrid>
              <ProcedureFooterComponent
                isPHI
                procedureType={'labs'}
                procedureComplications={procedureComplications}
                authorization={authorization}
                comments={comments}
              />
            </IonList>
          )}
        </>
      </IonContent>
    </IonPage>
  )
}

export default Labs
